import classNames from 'classnames'
import { t } from 'i18next'
import { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { getPolicy } from '../../../api/policyapi'
import childPlanFormDataContext, { IChildPlanFormData } from '../../../contexts/ChildPlanFormDataContext'
import ParentFormDataContext, {
    IParentFormData,
} from '../../../contexts/ParentFormDataContext'
import returnCustomerFormDataContext from '../../../contexts/ReturnCustomerFormDataContext'
import { ChildFormDataInputObject } from '../../../contexts/contextDataObjects/ChildDataObject'
import { ResponsiveWidth, disableProvinceDDL, disableSchoolBoardDDL, disableSchoolDDL } from '../../../contexts/contextDataObjects/ParentDataObject'
import {
    ICompanion,
    IInsured,
    IPolicyResponse,
} from '../../../model/IPolicyReponse'
import { fetchPolicyQuote } from '../../../utils/fetchPolicyQuote'
import { getDatePickerPattern, getSpecialCharacterPattern } from '../../../utils/patternHelper'
import { setRouteParam } from '../../../utils/routeHelper'
import Button from '../../button/Button'
import InputBox from '../../inputBox/InputBox'
import Modal from '../../modal/Modal'
import RadioGroup, { IRadioOption } from '../../radio/Radio'
import CircleSpinner from '../../spinners/circleSpinner/CircleSpinner'
export interface IReturnCustomer {
    className?: string
}

const ReturnCustomer: React.FC<IReturnCustomer> = ({ className }) => {
    const { returnCustomerFormData, setReturnCustomerFormData } = useContext(
        returnCustomerFormDataContext
    )

    const { parentFormData, setParentFormData } = useContext(
        ParentFormDataContext
    )

    const { childPlanFormData, setChildPlanFormData } = useContext(
        childPlanFormDataContext
    )

    const policyFormInput =
        returnCustomerFormData.ReturnCustomerFormDataInput.find(
            (x) => x.Id === 'ReturnPolicyNumInput'
        )

    const postalCodeFormInput =
        returnCustomerFormData.ReturnCustomerFormDataInput.find(
            (x) => x.Id === 'ReturnPostalCodeInput'
        )

    const [policyResponse, setPolicyResponse] = useState<IPolicyResponse>()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [modalText, setModalText] = useState('')
    const [isReturningCustomer, setIsReturningCustomer] = useState<number>(0)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    async function getPolicyData(policyNumber: string, postalCode: string) {
        try {
            setIsLoading(true)
            const policyData = await getPolicy(policyNumber, postalCode, parentFormData.Language)
            setPolicyResponse(policyData)

            if (postalCodeShouldMatchMainContactAddress(policyData)) {
                //Active, Auto-Renewal Policy's 
                if (
                    policyData?.policy?.billingtypecode === 'IMKANNUAL' &&
                    policyData?.policy?.status === 'ACTIVE'
                ) {
                    setModalText(
                        t('modalAutoRenewal_1') +
                        new Date(
                            policyData?.policy?.returndate
                        ).toDateString() +
                        t('modalAutoRenewal_2')
                    )
                    setIsModalOpen(true)
                    //Active, Non Auto-Renewal Policy's 
                } else if (
                    policyData?.policy?.status === 'ACTIVE' &&
                    new Date(policyData?.policy?.returndate) > new Date()
                ) {
                    setModalText(
                        t('modalActiveAndExpire_1') +
                        new Date(
                            policyData?.policy?.returndate
                        ).toDateString() +
                        t('modalActiveAndExpire_2')
                    )
                    setIsModalOpen(true)
                    //Expired, Non Auto-Renewal Policy's 
                } else if (
                    policyData?.policy?.status === 'ACTIVE' &&
                    new Date(policyData?.policy?.returndate) <= new Date()
                ) {
                    setModalText(
                        t('modalExpire_1') +
                        new Date(
                            policyData?.policy?.returndate
                        ).toDateString() +
                        t('modalExpire_2')
                    )
                    setIsModalOpen(true)
                }
            } else {
                toast.warn(t('errorPostal'))
            }
        } catch (error) {
            toast.error(t('errorNotLoadPolicy'))
        } finally {
            resetReturnCustomerFormData()
            setIsLoading(false)
        }
    }

    const postalCodeShouldMatchMainContactAddress = (
        policyResponse: IPolicyResponse
    ) => {
        if (
            policyResponse.contactAddress.postalcode
                .replace(' ', '')
                .toLowerCase()
                .trim() !==
            returnCustomerFormData.PostalCode.replace(' ', '')
                .toLowerCase()
                .trim()
        ) {
            return false
        }

        return true
    }

    const resetReturnCustomerFormData = () => {
        setReturnCustomerFormData({
            ...returnCustomerFormData,
            PolicyNumber: '',
            PostalCode: '',
        })
    }

    const setParentChildPlanInputs = (policyResponse: IPolicyResponse): { parent: IParentFormData, child: IChildPlanFormData[] } => {
        //Set ParentFormData
        const parent: ICompanion | undefined = policyResponse.companions.find(
            (x) => x.contacttype === 'PARENT'
        )

        const provinceItem =
            parentFormData.ParentFormDataInput.find(
                (x) => x.Name === 'Province'
            )?.ListItems?.find(
                (x) => x.id === policyResponse.contactAddress.provstate
            ) || parentFormData.Province

        setRouteParam(provinceItem.id, policyResponse.language, true)

        let autoRenewalData = { Renew: '', Index: -1, IsValid: false }
        if (policyResponse.policy.billingtypecode === 'IMKANNUAL') {
            autoRenewalData = { Renew: 'Yes', Index: 0, IsValid: true }
        } else {
            autoRenewalData = { Renew: 'No', Index: 1, IsValid: true }
        }

        let returnObject: { parent: IParentFormData, child: IChildPlanFormData[] } = {
            parent: parentFormData,
            child: childPlanFormData
        }
        let updateParentFormData: IParentFormData = { ...parentFormData }
        if (parent) {
            updateParentFormData = {
                ...parentFormData,
                Province: provinceItem,
                FirstName: parent.firstname,
                LastName: parent.lastname,
                Address: `${policyResponse.contactAddress.address1} ${policyResponse.contactAddress.address2}`,
                City: policyResponse.contactAddress.city,
                PostalCode: policyResponse.contactAddress.postalcode,
                PhoneNumber: policyResponse.contactInfo.dayphone,
                Email: policyResponse.contactInfo.emailaddress,
                RegionId: policyResponse.agency.regionId,
                ExternalRef: policyResponse.policyNum,
                SchoolBoard: {
                    id: policyResponse.agency.organizationId,
                    name: policyResponse.agency.organizationName,
                },
                PlanDuration: {
                    DurationText: '',
                    Duration: parseInt(
                        policyResponse.insureds[0].planCode.slice(-1)
                    ),
                    Discount: '',
                    Index: 0,
                },
                AutoEnroll: autoRenewalData,
            }

            updateParentFormData.ParentFormDataInput =
                updateParentFormData.ParentFormDataInput.map((input) => {
                    if (input.Name && updateParentFormData[input.Name]) {
                        let isValid = updateParentFormData[input.Name] !== ''

                        if (input.ControlType === 'DropDownList') {
                            isValid = updateParentFormData[input.Name].id !== ''
                        }

                        return {
                            ...input,
                            IsValid: isValid,
                            IsBlur: true,
                            ErrorMessage: isValid ? '' : (t('required')),
                        }
                    }
                    return input
                })

            updateParentFormData.CommonChildPlanFormInputData.IsValid =
                policyResponse.agency.organizationName !== ''
            updateParentFormData.CommonChildPlanFormInputData.IsBlur = true
            updateParentFormData.CommonChildPlanFormInputData.ErrorMessage =
                policyResponse.agency.organizationName !== '' ? '' : 'required'

            setParentFormData(updateParentFormData)
            returnObject.parent = updateParentFormData
        }
        //Set ChildPlanFormData 
        //SA54304.23 
        //N3S 4J4 
        const insureds: IInsured[] = policyResponse.insureds
        let newChildPlanFormData = [...childPlanFormData]

        insureds.map((insured, index) => {
            if (index === 0) {
                newChildPlanFormData[index].FirstName = insured.firstname
                newChildPlanFormData[index].LastName = insured.lastname
                newChildPlanFormData[index].Dob = new Date(insured.dob)

                newChildPlanFormData[index].School = {
                    id: insured.organizationSchoolId,
                    name:
                        insured.organizationSchoolName !== ''
                            ? insured.organizationSchoolName
                            : t('other'),
                }
                newChildPlanFormData[index].ChildPlanFormDataInput = ChildFormDataInputObject
                newChildPlanFormData[index].Plan.PlanCode = insured.planCode
            } else {
                const otherChildPlanFormData = {
                    FirstName: insured.firstname,
                    LastName: insured.lastname,
                    Dob: new Date(insured.dob),
                    School: {
                        id: insured.organizationSchoolId,
                        name:
                            insured.organizationSchoolName !== ''
                                ? insured.organizationSchoolName
                                : '(Other)',
                    },
                    Plan: {
                        PlanCode: insured.planCode,
                        Primary: '',
                        Premium: 0,
                        Index: 0,
                        IsValid: true
                    },
                    ChildPlanFormDataInput: ChildFormDataInputObject,
                    SetValidationFocus: false,
                }

                newChildPlanFormData.push(otherChildPlanFormData)
            }
        })

        const updatedFormData = newChildPlanFormData.map((childPlan) => {
            const updatedChildPlanData = childPlan.ChildPlanFormDataInput.map(
                (input) => {
                    if (input.Name && childPlan[input.Name]) {
                        let isValid = childPlan[input.Name] !== ''

                        if (input.ControlType === 'DropDownList') {
                            isValid = childPlan[input.Name].id !== ''
                        }

                        return {
                            ...input,
                            IsValid: childPlan[input.Name].id !== '',
                            IsBlur: true,
                            ErrorMessage: isValid ? '' : 'required',
                        }
                    }
                    return input
                }
            )

            return {
                ...childPlan,
                ChildPlanFormDataInput: updatedChildPlanData,
            }
        })

        setChildPlanFormData(updatedFormData)

        if (policyResponse.agency.regionId === 400002158) {
            disableProvinceDDL(updateParentFormData, setParentFormData)
            disableSchoolBoardDDL(setParentFormData)
            disableSchoolDDL(updatedFormData, setChildPlanFormData)
        }

        setReturnCustomerFormData({
            ...returnCustomerFormData,
            IsReturnCustomer: true,
        })
        returnObject.child = updatedFormData
        return returnObject
    }

    const resetParentChildPlanInputs = () => {
        setParentFormData({
            ...parentFormData,
            Province: parentFormData.Province,
            FirstName: '',
            LastName: '',
            Address: '',
            City: '',
            PostalCode: '',
            PhoneNumber: '',
            Email: '',
            DoNotOffer: false,
            OrgGuid: '',
            SchoolBoard: {
                id: '',
                name: '',
            },
            PlanDuration: {
                DurationText: '',
                Duration: 1,
                Discount: '',
                Index: 0,
            },
            AutoEnroll: {
                Renew: '',
                Index: -1,
                IsValid: false,
            },
        })

        const resetChildPlanFormData = [{
            FirstName: '',
            LastName: '',
            Dob: '',
            School: { id: '', name: '' },
            Plan: {
                PlanCode: '',
                Primary: '',
                Premium: 0,
                Index: -1,
                IsValid: false
            },
            ChildPlanFormDataInput: [{
                Id: 'ChildFnInput',
                Name: 'FirstName',
                Type: 'text',
                ControlType: 'InputBox',
                Placeholder: 'First Name.',
                Label: 'firstName',
                Required: true,
                ClassName: 'pt-2 lg:ml-2',
                Width: classNames(ResponsiveWidth, "border-[1.5px] border-gray-400"),
                Height: 'h-10',
                Shadow: 'shadow-md',
                ErrorMessageList: [
                    { type: 'required', error: 'required' },
                    { type: 'pattern', error: 'specialcharpatternerror' },
                ],
                ErrorMessage: '',
                Pattern: getSpecialCharacterPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'ChildLnInput',
                Name: 'LastName',
                Type: 'text',
                ControlType: 'InputBox',
                Placeholder: 'Last Name.',
                Label: 'lastName',
                Required: true,
                ClassName: 'pt-2',
                Width: classNames(ResponsiveWidth, "border-[1.5px] border-gray-400"),
                Height: 'h-10',
                Shadow: 'shadow-md',
                ErrorMessageList: [
                    { type: 'required', error: 'required' },
                    { type: 'pattern', error: 'specialcharpatternerror' },
                ],
                ErrorMessage: '',
                Pattern: getSpecialCharacterPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'ChildDobPicker',
                Name: 'Dob',
                Type: '',
                ControlType: 'DatePicker',
                Placeholder: 'selectDOB',
                Label: 'dob',
                Required: true,
                ClassName: classNames('mt-2 lg:mr-1.5 mr-0 '),
                Width: '',
                Height: 'h-10',
                Shadow: '',
                ErrorMessageList: [{ type: 'required', error: 'required' }],
                ErrorMessage: '',
                Pattern: getDatePickerPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'SchoolDDL',
                Name: 'School',
                Type: '',
                ControlType: 'DropDownList',
                ListItems: [{ id: '', name: '' }],
                Placeholder: '',
                Label: 'school',
                Required: true,
                ClassName: 'z-0 lg:mb-11 lg:pr-1 pr-0 lg:pt-1 ',
                Width: classNames(ResponsiveWidth),
                Height: 'h-[2.4rem]',
                Shadow: '',
                ErrorMessageList: [{ type: 'required', error: 'required' }],
                ErrorMessage: '',
                Pattern: '',
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            }],
            SetValidationFocus: false
        }]

        setChildPlanFormData(resetChildPlanFormData)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setReturnCustomerFormData({ ...returnCustomerFormData, [name]: value })
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        let isValid: boolean = true
        let errorMessage: string = ''

        const prevReturnCustomerFormDataInput =
            returnCustomerFormData.ReturnCustomerFormDataInput.find(
                (x) => x.Name === name
            )

        if (prevReturnCustomerFormDataInput != null) {
            // Validation - pattern match 
            if (returnCustomerFormData.PostalCode.trim() !== '') {
                const regex = RegExp(prevReturnCustomerFormDataInput.Pattern)

                if (
                    prevReturnCustomerFormDataInput.Pattern !== '' &&
                    !regex.test(value.trim())
                ) {
                    isValid = false
                    const temp =
                        prevReturnCustomerFormDataInput.ErrorMessageList.find(
                            (x) => x.type === 'pattern'
                        )

                    if (temp != null) {
                        errorMessage = temp.error
                    }
                } else {
                    isValid = true
                }
            }

            const updatedTemp = {
                ...prevReturnCustomerFormDataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }
            const updatedFormInputs = [
                ...returnCustomerFormData.ReturnCustomerFormDataInput,
            ]
            const index = updatedFormInputs.indexOf(
                prevReturnCustomerFormDataInput
            )
            updatedFormInputs[index] = updatedTemp
            setReturnCustomerFormData((prevState) => ({
                ...prevState,
                ReturnCustomerFormDataInput: updatedFormInputs,
            }))
        }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        const temp = returnCustomerFormData.ReturnCustomerFormDataInput.find(
            (x) => x.Name === name
        )

        if (temp != null) {
            const updatedTemp = { ...temp, IsBlur: false, IsFocus: true }
            const updatedFormInputs = [
                ...returnCustomerFormData.ReturnCustomerFormDataInput,
            ]
            const index = updatedFormInputs.indexOf(temp)
            updatedFormInputs[index] = updatedTemp
            setReturnCustomerFormData((prevState) => ({
                ...prevState,
                ReturnCustomerFormDataInput: updatedFormInputs,
            }))
        }
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        resetParentChildPlanInputs()
        getPolicyData(returnCustomerFormData.PolicyNumber.trim(), returnCustomerFormData.PostalCode.toUpperCase().trim())
    }

    const handleBtnDisabled = (): boolean => {
        if (
            returnCustomerFormData.PolicyNumber.trim() &&
            returnCustomerFormData.PostalCode.trim()
        ) {
            if (
                returnCustomerFormData.ReturnCustomerFormDataInput.every(
                    (x) => x.IsValid
                )
            ) {
                return false
            }
        }
        return true
    }

    const modalOptions: IRadioOption[] = [
        {
            label: t('usePolicy'),
            value: 0,
        },
        {
            label: t('startNewPolicy'),
            value: 1,
        },
    ]

    const handleCloseModal = () => {
        setIsModalOpen(false)
    }

    const hideReturningCustomer = () => {
        setReturnCustomerFormData((prevState) => ({
            ...prevState,
            ShowReturningCustomer: false,
        }))
    }


    const handleModalSelect = async (option: number) => {
        if (option == 0) {
            if (policyResponse) {
                const updatedData = setParentChildPlanInputs(policyResponse)
                fetchPolicyQuote(
                    updatedData.parent ? updatedData.parent : parentFormData,
                    updatedData.child ? updatedData.child : childPlanFormData,
                    setParentFormData,
                    setChildPlanFormData
                )
            }
        } else if (option == 1) {
            resetParentChildPlanInputs()
        }
        hideReturningCustomer()
        handleCloseModal()
    }

    return (
        <div className={classNames(className, "flex justify-center")}>
            <div className="bg-white lg:w-[55rem] md:w-[31rem] w-[20rem] shadow-md py-6 flex justify-center" >
                <div className='lg:w-[42rem] w-[18rem] relative' >
                    <h1 className="font-bold text-center pb-1 text-lg">
                        {t('returnCustomer')}
                    </h1>
                    {isReturningCustomer == 0 ? <div className='flex flex-row space-x-5 justify-center'>
                        <Button
                            className="bg-white lg:w-[15rem] md:w-[8rem] w-[6rem] h-10 hover:bg-gray-200 font-bold mt-6 cursor-pointer shadow-md border-[1.5px] border-gray-400"
                            textColor="text-black"
                            textSize="lg:text-sm md:text-sm text-xs"
                            text="Yes"
                            handleClick={() => setIsReturningCustomer(1)}
                        />
                        <Button
                            className="bg-white lg:w-[15rem] md:w-[8rem] w-[6rem] h-10 hover:bg-gray-200 font-bold mt-6 cursor-pointer shadow-md border-[1.5px] border-gray-400"
                            textColor="text-black"
                            textSize="lg:text-sm md:text-sm text-xs"
                            text="No"
                            handleClick={() => hideReturningCustomer()}
                        />
                    </div> : isReturningCustomer == 1 ? (<>
                        <div className="h-auto flex items-center lg:grid lg:grid-cols-2 lg:space-x-2 flex-col px-1">
                            <div>
                                {policyFormInput && (
                                    <InputBox
                                        key={policyFormInput.Id}
                                        type={policyFormInput.Type}
                                        inputID={policyFormInput.Id}
                                        label={t(policyFormInput.Label) || ''}
                                        width={policyFormInput.Width}
                                        height={policyFormInput.Height}
                                        className={policyFormInput.ClassName}
                                        shadow={policyFormInput.Shadow}
                                        onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => handleChange(e)}
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => handleBlur(e)}
                                        onFocus={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => handleFocus(e)}
                                        value={
                                            returnCustomerFormData[policyFormInput.Name]
                                        }
                                        name={policyFormInput.Name}
                                        required={policyFormInput.Required}
                                        error={policyFormInput.ErrorMessage}
                                        isValid={policyFormInput.IsValid}
                                        isBlur={policyFormInput.IsBlur}
                                        isFocus={policyFormInput.IsFocus}
                                    />
                                )}
                                <div className='flex justify-center '>
                                    <p className={classNames("text-xs text-gray-600 font-light lg:ml-0 ml-0.5", ResponsiveWidth)}>
                                        {t('dontRememberText')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-row lg:items-center lg:justify-center lg:space-x-3.5 lg:pb-8 ">
                                {postalCodeFormInput && (
                                    <InputBox
                                        key={postalCodeFormInput.Id}
                                        type={postalCodeFormInput.Type}
                                        inputID={postalCodeFormInput.Id}
                                        label={t(postalCodeFormInput.Label) || ''}
                                        width={postalCodeFormInput.Width}
                                        errorStyling={postalCodeFormInput.ErrorStyling}
                                        minWidth={postalCodeFormInput.MinWidth}
                                        height={postalCodeFormInput.Height}
                                        className={postalCodeFormInput.ClassName}
                                        shadow={postalCodeFormInput.Shadow}
                                        onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => handleChange(e)}
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => handleBlur(e)}
                                        onFocus={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => handleFocus(e)}
                                        value={
                                            returnCustomerFormData[
                                            postalCodeFormInput.Name
                                            ]
                                        }
                                        name={postalCodeFormInput.Name}
                                        required={postalCodeFormInput.Required}
                                        error={t(postalCodeFormInput.ErrorMessage) || ''}
                                        isValid={postalCodeFormInput.IsValid}
                                        isBlur={postalCodeFormInput.IsBlur}
                                        isFocus={postalCodeFormInput.IsFocus}
                                    />
                                )}
                                {isLoading ? <CircleSpinner size='50px' className='lg:w-[7rem] md:w-[6rem] w-[5rem] mt-[1.85rem] lg:ml-0 ml-4 lg:pl-2 md:pl-6 pl-4' /> : <Button
                                    className="bg-[#9ccb45] lg:w-[7rem] md:w-[6rem] w-[5rem] h-10 hover:bg-opacity-60 font-bold cursor-pointer shadow-md border-[1.5px] border-gray-400 lg:mt-6 mt-9 lg:ml-0 ml-4"
                                    textColor="text-white"
                                    textSize="lg:text-sm text-xs"
                                    text={t('confirm')}
                                    handleClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => handleClick(e)}
                                    disabled={handleBtnDisabled()}
                                />}


                            </div>
                        </div>
                        <div className='flex justify-center'>
                            <Button
                                className="bg-white hover:bg-gray-200 lg:w-[25rem] md:w-[17rem] w-[14rem] h-10 font-bold cursor-pointer shadow-md border-[1.5px] border-gray-400"
                                textColor="text-black"
                                textSize="lg:text-lg md:text-base text-sm"
                                text={t('proceedToParent')}
                                handleClick={() => hideReturningCustomer()}
                            />
                        </div>
                    </>
                    ) : <></>}
                </div>
            </div>

            <Modal
                title={t('policyNumber') + ": " + policyResponse?.policyNum}
                isOpen={isModalOpen}
                handleSave={handleCloseModal}
                height={'h-64'}
                width={'lg:w-[45rem] md:w-[25rem] w-[18rem]'}
            >
                <p className="pt-6 lg:text-base text-sm ">{modalText}</p>
                <div className="lg:mt-5 md:mt-3 mt-1 text-center">
                    <RadioGroup
                        className="items-center z-5"
                        buttonWidth="lg:w-[11rem] md:w-[9.5rem] w-[7rem]"
                        options={modalOptions}
                        handleSelectOption={handleModalSelect}
                    />
                </div>
            </Modal>
        </div >
    )
}

export default ReturnCustomer 
